import UI5Element from "@ui5/webcomponents-base/dist/UI5Element.js";
import Tokenizer from "@ui5/webcomponents/dist/Tokenizer.js";
import ResizeHandler from "@ui5/webcomponents-base/dist/delegate/ResizeHandler.js";
import customElement from "@ui5/webcomponents-base/dist/decorators/customElement.js";
import { isEnter, isSpace, isEscape } from "@ui5/webcomponents-base/dist/Keys.js";
import property from "@ui5/webcomponents-base/dist/decorators/property.js";
import slot from "@ui5/webcomponents-base/dist/decorators/slot.js";
import event from "@ui5/webcomponents-base/dist/decorators/event.js";
import litRender from "@ui5/webcomponents-base/dist/renderer/LitRenderer.js";
import Token from "@ui5/webcomponents/dist/Token.js";
import UDExTag from "./Tag.js";
import UDExTooltip from "./Tooltip.js";

import TagGroupTemplate from "./generated/templates/TagGroupTemplate.lit.js";

// Styles
import TagGroupCss from "./generated/themes/TagGroup.css";

export enum PlacementType {
  Left = "Left",
  Right = "Right",
  Top = "Top",
  Bottom = "Bottom",
}

/**
 * @class
 *
 * <h3 class="comment-api-title">Overview</h3>
 *
 *
 * <h3>Usage</h3>
 *
 * For the <code>udex-tag-group</code>
 * <h3>ES6 Module Import</h3>
 *
 * <code>import "@udex/web-components/dist/TagGroup.js";</code>
 *
 * @constructor
 * @extends UI5Element
 * @public
 */
@customElement({
  tag: "udex-tag-group",
  renderer: litRender,
  styles: TagGroupCss,
  template: TagGroupTemplate,
  dependencies: [UDExTag, UDExTooltip, Tokenizer, Token],
})
/**
 * Emitted when tag was clicked
 *
 * @public
 */
@event("tag-click")
/**
 * Emitted when icon in tag was clicked
 *
 * @public
 */
@event("tag-icon-click")
/**
 * Emitted when the tooltip was opened, can be canceled
 *
 * @public
 */
@event("show-more-tags")
/**
 * Emitted  when a keydown event occurs on a tag, can be canceled
 * Emitted custom event with keycode and tag details
 *
 * @public
 */
@event("tag-keydown")
class UDExTagGroup extends UI5Element {
  /**
   * Defines whether the UI5Tokenizer is expanded.
   *
   * @default false
   * @public
   */
  @property({ type: Boolean })
    tokenizerExpanded!: boolean;
  /**
   * Defines whether the component is in disabled state.
   *
   * @default false
   * @public
   */
  @property({ type: Boolean })
    disabled!: boolean;

  /**
   * Defines whether the component is readonly.
   *
   * @default false
   * @public
   */
  @property({ type: Boolean })
    readonly!: boolean;

  /**
   * Defines whether the component is displayonly.
   *
   * @default false
   * @public
   */
  @property({ type: Boolean })
    displayonly!: boolean;

  /**
   * Defines the tags.
   *
   * @public
   */
  @slot({ type: HTMLElement, "default": true })
    items!: Array<UDExTag>;

  /**
   * Determines on which side of the component the Tooltip is displayed.
   *
   * @default "Bottom"
   * @public
   */
  @property({ type: PlacementType, defaultValue: PlacementType.Bottom })
    tooltipPlacement!: `${PlacementType}`;

  /**
   * @private
   */
  @property({ type: Boolean })
    _isHiddenTags!: boolean;

  _tokenizer!: Tokenizer;
  _nMoreBtn!: HTMLElement | null;
  _resizeHandler: () => void;

  constructor() {
    super();
    this._resizeHandler = this._onResize.bind(this);
  }

  get hiddenTags(): Token[] {
    return this._tokenizer?.overflownTokens || [];
  }

  get isReadOrDisplayOnly(): boolean {
    return this.readonly || this.displayonly;
  }

  get tagTabIndex(): string {
    return this.disabled ? "-1" : "";
  }

  get tokenizer(): Tokenizer {
    return this.shadowRoot!.querySelector<Tokenizer>(`#${this._id}-tag-group-tokenizer`)!;
  }

  get tagRole(): string {
    return this.displayonly || this.readonly ? "option" : "";
  }

  onAfterRendering(): void {
    this._tokenizer = this.tokenizer;
    this._nMoreBtn = this._tokenizer.shadowRoot?.querySelector(`.ui5-tokenizer-more-text`) || null;
    if (!this.readonly && !this.disabled && this._nMoreBtn && !this._nMoreBtn.hasAttribute("tabindex")) {
      this._nMoreBtn.setAttribute("tabindex", "0");
      this._nMoreBtn.addEventListener("keydown", this._nMoreKeyDownHandler.bind(this));
    }
  }

  _nMoreKeyDownHandler(e: Event) {
    if (isSpace(e as KeyboardEvent) || isEnter(e as KeyboardEvent)) {
      this.showMoreItems();
    }
  }

  onEnterDOM(): void {
    ResizeHandler.register(this, this._resizeHandler);
  }

  onExitDOM(): void {
    ResizeHandler.deregister(this, this._resizeHandler);
    this._nMoreBtn?.removeEventListener("keydown", this._nMoreKeyDownHandler.bind(this));
  }

  _onResize(): void {
    this._isHiddenTags = false;
    this._isHiddenTags = this._tokenizer?._nMoreCount > 0;
  }

  tagClickHandler(e: CustomEvent): void {
    this.fireEvent("tag-click", { tag: e.target as HTMLElement });
  }

  tagIconClickHandler(e: CustomEvent): void {
    this.fireEvent("tag-icon-click", { tag: e.target as HTMLElement });
  }

  handleShowMoreButtonKeydown(e: KeyboardEvent): void {
    if (isEscape(e)) {
      this._nMoreBtn?.focus();
    }
  }

  handleTagKeyDown(e: KeyboardEvent): void {
    this.fireEvent("tag-keydown", { originalEvent: e }, true);
  }

  showMoreItems(): void {
    if (this.fireEvent("show-more-tags", {}, true)) {
      const tooltip = this.shadowRoot!.querySelector<UDExTooltip>(`#${this._id}-tag-group-tooltip`)!;
      if (this._nMoreBtn) {
        tooltip?.onOpen(this._nMoreBtn);
      }
    }
  }
}

UDExTagGroup.define();

export default UDExTagGroup;
